import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CalendarDays, Paperclip, Plus } from "lucide-react";
import { PhoneFrame, PhoneAppBar } from "@/components/drift/PhoneFrame";
import { MontorFeed } from "@/components/drift/MontorFeed";
import { api } from "@/lib/api";
import { fmtDayLong, relDay, timeAgo, MARKET, ORDER_TYPE, PRIORITY } from "@/lib/format";
import { OrderStatus, Priority, Chip } from "@/components/drift/StatusBadge";
import { KpiTile, KpiStrip, Panel, EmptyState, LoadingRows, ErrorState, Avatar } from "@/components/drift/common";
import { CompanyMark } from "@/components/drift/CompanyMark";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

/* Severity of an attention item → dot tone. */
const SEV_DOT = { kritisk: "bg-danger-dot", paagaar: "bg-active-dot", planlagt: "bg-info-dot", ny: "bg-info-dot" };
/* Order status → dot on the timeline. */
const STATUS_DOT = { ny: "bg-info-dot", planlagt: "bg-info-dot", paagaar: "bg-active-dot", ferdig: "bg-ok-dot", fakturert: "bg-foreground" };

/* Live phone preview of one technician's day, with a row of avatars to switch. */
function FeltPanel() {
  const [plan, setPlan] = useState(null);
  const [sel, setSel] = useState(null);
  useEffect(() => { api.get("/drift/planning").then((r) => setPlan(r.data)).catch(() => setPlan({ rows: [] })); }, []);
  const rows = useMemo(() => (plan?.rows || []).slice().sort((a, b) => b.orders.length - a.orders.length || a.user.sort - b.user.sort), [plan]);
  const active = rows.find((r) => r.user.id === sel) || rows[0];
  return (
    <Panel title="Felt no" subtitle={active ? `${active.user.name} · ${active.orders.length} oppdrag i dag` : "Montørane sin dag på telefonen"} bodyClass="p-0" testid="dashboard-felt-panel" className="xl:self-start"
      action={<PanelLink to="/drift/felt" testid="dashboard-felt-link">Feltvising</PanelLink>}>
      <div className="flex items-center gap-1 overflow-x-auto px-4 py-3 scrollbar-none" data-testid="dashboard-felt-switcher">
        {rows.slice(0, 8).map((r) => {
          const on = r.user.id === active?.user.id;
          return (
            <button key={r.user.id} type="button" onClick={() => setSel(r.user.id)} title={`${r.user.name} · ${r.orders.length} oppdrag`} aria-pressed={on} className={cn("relative shrink-0 rounded-lg p-0.5 transition-[transform,opacity] duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring", on ? "scale-105" : "opacity-60 hover:opacity-100")} data-testid="dashboard-felt-avatar">
              <Avatar name={r.user.name} initials={r.user.initials} tone={on ? "dark" : "soft"} />
              {on && <span className="absolute -bottom-1 left-1/2 h-1 w-1 -translate-x-1/2 rounded-full bg-brand" aria-hidden />}
            </button>
          );
        })}
      </div>
      <div className="flex justify-center px-4 pb-4 pt-1">
        <PhoneFrame scale={0.7} time={new Date().toTimeString().slice(0, 5)}>
          {active ? <><PhoneAppBar user={active.user} /><MontorFeed key={active.user.id} assignedTo={active.user.id} name={active.user.name} embedded /></> : <div className="p-5"><Skeleton className="h-6 w-40 rounded-md bg-secondary" /></div>}
        </PhoneFrame>
      </div>
    </Panel>
  );
}

const PanelLink = ({ to, children, testid }) => (
  <Link to={to} className="link-arrow inline-flex items-center gap-1 rounded-md text-[12.5px] font-medium text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" data-testid={testid}>
    {children} <ArrowRight className="h-3.5 w-3.5" strokeWidth={1.75} aria-hidden />
  </Link>
);

export default function Dashboard() {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);

  const load = () => api.get("/drift/dashboard").then((r) => { setD(r.data); setErr(null); }).catch((e) => setErr(e));
  useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); }, []);

  if (err) return <ErrorState title="Fekk ikkje lasta oversikta" onRetry={load} />;
  if (!d) {
    return (
      <div data-testid="drift-dashboard-loading">
        <Skeleton className="h-4 w-40 rounded-md bg-secondary" />
        <Skeleton className="mt-3 h-9 w-72 rounded-md bg-secondary" />
        <div className="mt-7 grid grid-cols-2 gap-px overflow-hidden rounded-xl bg-border/70 md:grid-cols-3 xl:grid-cols-6">
          {Array.from({ length: 6 }).map((_, i) => <div key={i} className="bg-card p-4"><Skeleton className="h-3.5 w-24 rounded-md bg-secondary" /><Skeleton className="mt-4 h-8 w-12 rounded-md bg-secondary" /></div>)}
        </div>
        <div className="mt-6 grid grid-cols-1 gap-5 xl:grid-cols-3"><LoadingRows n={6} header className="xl:col-span-2" /><LoadingRows n={5} header /></div>
      </div>
    );
  }

  const k = d.kpis;
  const maxWeek = Math.max(1, ...d.week.map((w) => w.count));

  return (
    <div data-testid="drift-dashboard">
      <div className="mb-7 flex flex-col gap-5 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="eyebrow">{fmtDayLong(d.date)}</p>
          <h1 className="mt-1 font-display text-3xl font-medium tracking-[-0.025em] sm:text-4xl" data-testid="drift-dashboard-greeting">{d.greeting}, {d.first_name}.</h1>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button asChild variant="outline" className="h-9 rounded-lg bg-card"><Link to="/drift/planlegging" data-testid="drift-dashboard-planning-button"><CalendarDays className="mr-1.5 h-4 w-4" strokeWidth={1.75} /> Planlegging</Link></Button>
          <Button asChild className="h-9 rounded-lg bg-brand text-brand-foreground hover:bg-brand-dark"><Link to="/drift/oppdrag?new=1" data-testid="drift-dashboard-new-order-button"><Plus className="mr-1.5 h-4 w-4" strokeWidth={2} /> Nytt oppdrag</Link></Button>
        </div>
      </div>

      <KpiStrip testid="drift-kpi-grid">
        <KpiTile flat label="Aktive oppdrag" value={k.active_orders} to="/drift/oppdrag?status=planlagt,paagaar" testid="kpi-active-orders" />
        <KpiTile flat label="Montørar ute i dag" value={k.montorar_ute} to="/drift/planlegging" testid="kpi-montorar-ute" />
        <KpiTile flat label="Manglar dokumentasjon" value={k.missing_docs} to="/drift/fakturagrunnlag" tone={k.missing_docs > 0 ? "warn" : "default"} testid="kpi-missing-docs" />
        <KpiTile flat label="Nye henvendingar" value={k.new_inquiries} to="/drift/henvendingar?status=ny" tone={k.new_inquiries > 0 ? "brand" : "default"} testid="kpi-new-inquiries" />
        <KpiTile flat label="Tilbod å følgje opp" value={k.quotes_followup} to="/drift/tilbod" tone={k.quotes_followup > 0 ? "warn" : "default"} testid="kpi-quotes-followup" />
        <KpiTile flat label="Prosjekt over budsjett" value={k.projects_over_budget} to="/drift/oppdrag?status=paagaar" tone={k.projects_over_budget > 0 ? "danger" : "default"} testid="kpi-over-budget" />
      </KpiStrip>

      <div className="mt-6 grid grid-cols-1 gap-5 xl:grid-cols-3">
        {/* Today as a timeline */}
        <Panel
          title="I dag"
          subtitle={`${d.today_orders.length} oppdrag · ${k.montorar_ute} montørar ute`}
          className="xl:col-span-2"
          bodyClass="p-0"
          testid="dashboard-today-panel"
          action={<PanelLink to="/drift/planlegging" testid="dashboard-today-planning-link">Planlegging</PanelLink>}
        >
          {d.today_orders.length === 0 ? (
            <div className="p-4"><EmptyState compact icon={CalendarDays} title="Ingen oppdrag planlagt i dag" text="Gå til planlegging for å tildele oppdrag." action={<Button asChild variant="outline" size="sm" className="rounded-lg bg-card"><Link to="/drift/planlegging">Opne planlegging</Link></Button>} /></div>
          ) : (
            <ol className="relative px-5 py-2 before:absolute before:bottom-5 before:left-[83px] before:top-5 before:w-px before:bg-border sm:before:left-[91px]" data-testid="drift-today-timeline">
              {d.today_orders.map((o) => (
                <li key={o.id} className="relative">
                  <Link to={`/drift/oppdrag/${o.id}`} className="group grid grid-cols-[48px_20px_1fr_auto] items-center gap-x-3 rounded-lg py-3 pr-1 transition-colors duration-150 hover:bg-accent/30 sm:grid-cols-[56px_20px_1fr_168px_auto] sm:gap-x-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" data-testid="dashboard-today-order-row">
                    <span className="tabular font-mono text-[12.5px] text-muted-foreground">{o.scheduled_time || "–"}</span>
                    <span className="flex justify-center"><span className={cn("h-2.5 w-2.5 rounded-full ring-4 ring-card", STATUS_DOT[o.status] || "bg-info-dot")} aria-hidden /></span>
                    <span className="flex min-w-0 items-center gap-3">
                      <CompanyMark name={o.customer_name} brand={o.customer_brand} type={o.customer_type} size="md" className="hidden sm:inline-flex" />
                      <span className="min-w-0">
                        <span className="flex items-center gap-2">
                          <span className="truncate text-[14px] font-medium">{o.title}</span>
                          <Priority priority={o.priority} hideNormal />
                        </span>
                        <span className="block truncate text-[12.5px] text-muted-foreground">{o.customer_name} · {o.location_address || o.location_name}</span>
                      </span>
                    </span>
                    <span className="hidden min-w-0 items-center gap-2 text-[12.5px] text-muted-foreground sm:flex">
                      {o.assigned_name ? <><Avatar name={o.assigned_name} initials={o.assigned_initials} size="sm" tone="soft" /> <span className="truncate">{o.assigned_name}</span></> : <span className="inline-flex items-center gap-1.5 text-warn"><span className="h-1.5 w-1.5 rounded-full bg-warn-dot" aria-hidden />Ikkje tildelt</span>}
                    </span>
                    <OrderStatus status={o.status} size="sm" />
                  </Link>
                </li>
              ))}
            </ol>
          )}
        </Panel>

        <Panel title="Treng merksemd" subtitle={d.attention.length ? `${d.attention.length} punkt` : "Alt under kontroll"} bodyClass="p-0" testid="dashboard-attention-panel">
          {d.attention.length === 0 ? (
            <div className="p-4"><EmptyState compact title="Alt under kontroll" text="Ingenting som treng merksemd akkurat no." /></div>
          ) : (
            <ul className="p-2">
              {d.attention.map((a, i) => (
                <li key={i}>
                  <Link to={a.link} className="flex items-start gap-3 rounded-lg px-3 py-2.5 transition-colors duration-150 hover:bg-accent/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" data-testid="dashboard-attention-item">
                    <span className={cn("mt-[7px] h-2 w-2 shrink-0 rounded-full", SEV_DOT[a.severity] || SEV_DOT.ny)} aria-hidden />
                    <span className="min-w-0">
                      <span className="block truncate text-[13.5px] font-medium">{a.title}</span>
                      <span className="block truncate text-[12.5px] text-muted-foreground">{a.subtitle}</span>
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </Panel>
      </div>

      <div className="mt-5 grid grid-cols-1 gap-5 xl:grid-cols-3">
        <FeltPanel />
        <Panel title="Nye henvendingar" subtitle="Frå nettsida og e-post" className="xl:col-span-2 xl:row-span-2" bodyClass="p-0" testid="dashboard-inquiries-panel"
          action={<PanelLink to="/drift/henvendingar" testid="dashboard-inquiries-all-link">Alle</PanelLink>}>
          {d.new_inquiries.length === 0 ? (
            <div className="p-4"><EmptyState compact title="Ingen nye henvendingar" text="Nye førespurnader frå nettsida dukkar opp her." /></div>
          ) : (
            <ul className="divide-y divide-border/60">
              {d.new_inquiries.map((i) => (
                <li key={i.id}>
                  <Link to={`/drift/henvendingar/${i.id}`} className="block px-5 py-3.5 transition-colors duration-150 hover:bg-accent/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" data-testid="dashboard-inquiry-row">
                    <div className="flex items-center justify-between gap-3">
                      <span className="truncate text-[14px] font-medium">{i.company || i.name}</span>
                      <span className="flex shrink-0 items-center gap-3 text-[12px] text-muted-foreground">
                        {i.attachment_count > 0 && <span className="inline-flex items-center gap-1"><Paperclip className="h-3 w-3" strokeWidth={1.75} aria-hidden />{i.attachment_count}</span>}
                        <span className="font-mono tabular">{i.ref}</span>
                        <span>{timeAgo(i.created_at)}</span>
                      </span>
                    </div>
                    <p className="mt-1 line-clamp-1 text-[13px] text-muted-foreground">{i.message}</p>
                    {i.ai_status === "done" && i.ai && (
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        <Chip>{MARKET[i.ai.marknad]}</Chip>
                        <Chip>{ORDER_TYPE[i.ai.oppdragstype]}</Chip>
                        {i.ai.hastegrad !== "normal" && <Chip className={i.ai.hastegrad === "akutt" ? "tone-danger" : "tone-warn"}>{PRIORITY[i.ai.hastegrad]}</Chip>}
                      </div>
                    )}
                    {i.ai_status === "pending" && <p className="t-mono mt-2">Les gjennom …</p>}
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </Panel>

        <Panel title="Dei neste 7 dagane" subtitle="Planlagde oppdrag per dag" className="xl:self-start" testid="dashboard-week-panel">
          <div className="flex h-40 items-end gap-2">
            {d.week.map((w, idx) => {
              const h = Math.max(6, (w.count / maxWeek) * 104);
              return (
                <Link to={`/drift/planlegging?date=${w.date}`} key={w.date} className="group flex flex-1 flex-col items-center gap-2 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" title={`${w.count} oppdrag`} data-testid="dashboard-week-bar">
                  <span className="tabular font-mono text-[11.5px] text-muted-foreground">{w.count}</span>
                  <span className="flex w-full items-end rounded-md bg-secondary" style={{ height: "104px" }}>
                    <span className={cn("w-full rounded-md transition-colors duration-150", idx === 0 ? "bg-brand" : "bg-foreground/75 group-hover:bg-brand")} style={{ height: `${h}px` }} />
                  </span>
                  <span className={cn("text-[11.5px]", idx === 0 ? "font-medium text-foreground" : "text-muted-foreground")}>{relDay(w.date).slice(0, 7)}</span>
                </Link>
              );
            })}
          </div>
          <p className="mt-5 border-t border-border/70 pt-4 text-[13px] text-muted-foreground">
            <span className="tabular font-medium text-foreground">{k.invoice_ready}</span> oppdrag klare for fakturagrunnlag.{" "}
            <Link to="/drift/fakturagrunnlag" className="u-link font-medium text-foreground" data-testid="dashboard-invoice-ready-link">Sjå grunnlag</Link>
          </p>
        </Panel>
      </div>
    </div>
  );
}
